import setCookie from '../shared/helpers/cookie'
import { setSessionStorageItem, getSessionStorageItem } from '../shared/helpers/sessionStorage'
import handleReducerSimpleSetter from '../shared/helpers/handleReducerSimpleSetter'
import getReducerHandler from '../shared/helpers/getReducerHandler'

const ACTIONS = Object.freeze({
  UPDATE_LANGUAGE: 'UPDATE_LANGUAGE',
  CLEAR_NONSTANDARD_USER: 'CLEAR_NONSTANDARD_USER',
  POPULATE_NONSTANDARD_USER: 'POPULATE_NONSTANDARD_USER',
  SET_HEADER_FOOTER: 'SET_HEADER_FOOTER',
  SET_STATION_MODE: 'SET_STATION_MODE',
  SET_APPLAYOUT: 'SET_APPLAYOUT',
  STATE_UPDATE: 'STATE_UPDATE',
})

const getShowHFInitially = () => {
  const mode = getSessionStorageItem('mode') ?? 'WEB'
  const isWebMode = mode === 'WEB'
  const pathArray = ['/kioskcodeentry', '/discoverycenteruser', '/about/correctionslogin'] // Prevent a flash of the header and footer on these pages. Sessionstorage will have been cleared for discovery stations in kioware, so we won't have the mode set immediately
  return isWebMode && !pathArray.some((p) => window.location.pathname.endsWith(p))
}

// Shared by getDSCInitialState and handleClearNonstandardUser: consumers
// like useAamGuestInitialization read nonstandardUser.initialized/.authenticated
// etc. unconditionally (including from effect dependency arrays, which
// evaluate before any null-guard in the effect body can run), so
// nonstandardUser must always be a well-formed object, never null/undefined.
const emptyNonstandardUser = () => ({
  firstName: '',
  lastName: '',
  displayName: '',
  birthYear: null,
  genderType: 'unknown',
  initialized: false,
  languageRegion: '',
  authenticated: false,
  email: '',
  atn: 1,
})

const shouldClearNonstandardUser = (nonstandardUser) =>
  Boolean(nonstandardUser?.initialized && !nonstandardUser?.birthYear)

const getDSCInitialState = ({ cachedNonstandardUser, cachedCountry }) => {
  const showHFInitially = getShowHFInitially()

  return {
    showHeader: showHFInitially,
    showFooter: showHFInitially,
    appLayout: {
      fullWidth: true,
    },
    hasSetToStationOrKiosk: false,
    // A count rather than a boolean: multiple <LeaveKioskButton inline />
    // instances can be mounted at once (e.g. Time Machine renders one in its
    // selector view and one in its lineage panel, both briefly mounted
    // during a transition). A boolean would let one instance's unmount
    // re-enable ConditionalHeader's floating copy while another inline
    // instance is still on screen.
    customCountry: cachedCountry ?? null,
    nonstandardUser: cachedNonstandardUser ?? emptyNonstandardUser(),
  }
}

/*
 * --- INDIVIDUAL ACTION HANDLERS ---
 * (Set multiple values & merge/processing logic only, otherwise use handleReducerSimpleSetter)
 */

const handleUpdateLanguage = (state, action) => {
  const countryLang = action?.data?.countryLang?.value?.split('_')
  setCookie('fslanguage', countryLang?.[1] ?? 'en')
  const customCountry = countryLang?.[0] ?? 'us'
  setSessionStorageItem({ customCountry })
  return { ...state, customCountry }
}

const handleClearNonstandardUser = (state) => {
  // Reset to the well-formed placeholder rather than null: AppAam.js treats
  // a falsy nonstandardUser as "fall through to the signed-in AAMMain view,"
  // which is wrong for a guest whose data just got cleared for being
  // incomplete - it should keep seeing the guest flow so it can prompt them
  // to re-enter their info, not spin forever waiting on a real sign-in.
  const nonstandardUser = emptyNonstandardUser()
  setSessionStorageItem({ nonstandardUser })
  return { ...state, nonstandardUser }
}

const handlePopulateNonstandardUser = (state, action) => {
  const nonstandardUser = action?.data?.nonstandardUser ?? {}
  let displayName = nonstandardUser?.displayName ?? ''
  if (!displayName && nonstandardUser.firstName && nonstandardUser.lastName) {
    displayName = `${nonstandardUser.firstName} ${nonstandardUser.lastName}`
  }
  nonstandardUser.initialized = true
  nonstandardUser.genderType = 'unknown'
  nonstandardUser.displayName = displayName
  nonstandardUser.authenticated = action?.data?.nonstandardUser?.authenticated ?? false
  nonstandardUser.atn = 1
  const countryLang = nonstandardUser?.languageRegion?.value?.split('_')
  let customCountry = state.customCountry
  if (countryLang) {
    customCountry = countryLang?.[0] ?? 'us'
    setCookie('fslanguage', countryLang?.[1] ?? 'en')
    setSessionStorageItem({ customCountry })
  }
  const next = { ...state, ...action.data, customCountry }
  setSessionStorageItem(next)
  setSessionStorageItem({ redirectToGuestPage: true })
  setSessionStorageItem({ guestTree: [nonstandardUser] })
  return next
}

const handleSetHeaderFooter = (state, action) => ({
  ...state,
  showHeader: action.data.showHeader,
  showFooter: action.data.showFooter,
})

const handleSetStationMode = (state, action) => ({
  ...state,
  showHeader: action.data.showHeader,
  showFooter: action.data.showFooter,
  appLayout: { ...action.data.appLayout },
  hasSetToStationOrKiosk: true,
})

// --- Handlers registry ---
const handlers = {
  [ACTIONS.UPDATE_LANGUAGE]: handleUpdateLanguage,
  [ACTIONS.CLEAR_NONSTANDARD_USER]: handleClearNonstandardUser,
  [ACTIONS.POPULATE_NONSTANDARD_USER]: handlePopulateNonstandardUser,
  [ACTIONS.SET_HEADER_FOOTER]: handleSetHeaderFooter,
  [ACTIONS.SET_STATION_MODE]: handleSetStationMode,
  [ACTIONS.SET_APPLAYOUT]: handleReducerSimpleSetter,
  [ACTIONS.STATE_UPDATE]: handleReducerSimpleSetter,
}

const DSCReducer = (state, action) => getReducerHandler(state, action, handlers)

export { getDSCInitialState, DSCReducer, shouldClearNonstandardUser, ACTIONS }
